import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { Submittal, RFI } from '../types';

// ── Submittals ─────────────────────────────────────────────────────

const SUBMITTAL_FIELDS = [
  'dagrun_pm_submittalid', 'dagrun_pm_name', 'dagrun_pm_submittalnumber',
  'dagrun_pm_description', 'dagrun_pm_spec',
  'dagrun_pm_submittedbyid', 'dagrun_pm_submittedbyname',
  'dagrun_pm_submitteddate', 'dagrun_pm_requireddate',
  'dagrun_pm_status',
  'dagrun_pm_reviewerid', 'dagrun_pm_reviewername', 'dagrun_pm_revieweddate',
  'dagrun_pm_notes', 'dagrun_pm_projectid',
  'createdon',
];

export async function getProjectSubmittals(pca: IPublicClientApplication, projectId: string): Promise<Submittal[]> {
  return dvList<Submittal>(pca, 'dagrun_pm_submittals', {
    select: SUBMITTAL_FIELDS,
    filter: `dagrun_pm_projectid eq '${projectId}'`,
    orderby: 'dagrun_pm_submitteddate desc',
    top: 500,
  });
}

export interface CreateSubmittalInput {
  projectId: string;
  name: string;                 // description (required field)
  submittalnumber?: string | null;
  spec?: string | null;
  submittedbyid?: string | null;
  submittedbyname?: string | null;
  submitteddate?: string | null;
  requireddate?: string | null;
  status?: number;
  notes?: string | null;
}

export async function createSubmittal(pca: IPublicClientApplication, input: CreateSubmittalInput): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: input.name,
    dagrun_pm_projectid: input.projectId,
    dagrun_pm_status: input.status ?? 1, // Draft
  };
  if (input.submittalnumber)   body.dagrun_pm_submittalnumber = input.submittalnumber;
  if (input.spec)              body.dagrun_pm_spec = input.spec;
  if (input.submittedbyid)     body.dagrun_pm_submittedbyid = input.submittedbyid;
  if (input.submittedbyname)   body.dagrun_pm_submittedbyname = input.submittedbyname;
  if (input.submitteddate)     body.dagrun_pm_submitteddate = input.submitteddate;
  if (input.requireddate)      body.dagrun_pm_requireddate = input.requireddate;
  if (input.notes)             body.dagrun_pm_notes = input.notes;
  return dvCreate(pca, 'dagrun_pm_submittals', body);
}

export async function updateSubmittal(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_submittals', id, patch);
}

export async function deleteSubmittal(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_submittals', id);
}

// Suggest the next Submittal/RFI number by finding the max of existing
// {prefix}### patterns and incrementing. Returns a string like "S-003".
export function suggestNextNumber(prefix: string, existing: (string | null | undefined)[]): string {
  const re = new RegExp(`^${prefix.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}(\\d+)$`, 'i');
  let maxN = 0;
  existing.forEach(v => {
    if (!v) return;
    const m = v.match(re);
    if (m) {
      const n = parseInt(m[1], 10);
      if (!isNaN(n) && n > maxN) maxN = n;
    }
  });
  const next = maxN + 1;
  return `${prefix}${String(next).padStart(3, '0')}`;
}

// ── RFIs ──────────────────────────────────────────────────────────

const RFI_FIELDS = [
  'dagrun_pm_rfiid', 'dagrun_pm_name', 'dagrun_pm_rfinumber',
  'dagrun_pm_question',
  'dagrun_pm_submittedbyid', 'dagrun_pm_submittedbyname',
  'dagrun_pm_submitteddate', 'dagrun_pm_duedate',
  'dagrun_pm_status',
  'dagrun_pm_assignedtoid', 'dagrun_pm_assignedtoname',
  'dagrun_pm_answer', 'dagrun_pm_answereddate',
  'dagrun_pm_priority', 'dagrun_pm_projectid',
  'createdon',
];

export async function getProjectRFIs(pca: IPublicClientApplication, projectId: string): Promise<RFI[]> {
  return dvList<RFI>(pca, 'dagrun_pm_rfis', {
    select: RFI_FIELDS,
    filter: `dagrun_pm_projectid eq '${projectId}'`,
    orderby: 'dagrun_pm_submitteddate desc',
    top: 500,
  });
}

export interface CreateRFIInput {
  projectId: string;
  name: string;                 // short question title
  rfinumber?: string | null;
  question?: string | null;     // full-text question body
  submittedbyid?: string | null;
  submittedbyname?: string | null;
  submitteddate?: string | null;
  duedate?: string | null;
  status?: number;
  assignedtoid?: string | null;
  assignedtoname?: string | null;
  priority?: number;
}

export async function createRFI(pca: IPublicClientApplication, input: CreateRFIInput): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: input.name,
    dagrun_pm_projectid: input.projectId,
    dagrun_pm_status: input.status ?? 1,    // Open
    dagrun_pm_priority: input.priority ?? 2, // Medium default
  };
  if (input.rfinumber)         body.dagrun_pm_rfinumber = input.rfinumber;
  if (input.question)          body.dagrun_pm_question = input.question;
  if (input.submittedbyid)     body.dagrun_pm_submittedbyid = input.submittedbyid;
  if (input.submittedbyname)   body.dagrun_pm_submittedbyname = input.submittedbyname;
  if (input.submitteddate)     body.dagrun_pm_submitteddate = input.submitteddate;
  if (input.duedate)           body.dagrun_pm_duedate = input.duedate;
  if (input.assignedtoid)      body.dagrun_pm_assignedtoid = input.assignedtoid;
  if (input.assignedtoname)    body.dagrun_pm_assignedtoname = input.assignedtoname;
  return dvCreate(pca, 'dagrun_pm_rfis', body);
}

export async function updateRFI(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_rfis', id, patch);
}

export async function deleteRFI(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_rfis', id);
}
